import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

export default function InspireMode() {
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [index, setIndex] = useState(0);
  const [flags, setFlags] = useState({});
  const [loading, setLoading] = useState(true);
  const [started, setStarted] = useState(false);
  const [done, setDone] = useState(false);
  const [filterTag, setFilterTag] = useState('');
  const [tags, setTags] = useState([]);
  const [swipeClass, setSwipeClass] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportDone, setExportDone] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [tagging, setTagging] = useState(false);
  const [tagSuccess, setTagSuccess] = useState(false);
  const [showConfirmReset, setShowConfirmReset] = useState(false);
  const [serverHasUnfinished, setServerHasUnfinished] = useState(false);
  const [unfinishedCount, setUnfinishedCount] = useState(0);
  const [yesCount, setYesCount] = useState(0);
  const [navGuard, setNavGuard] = useState(null); // { destination } | null
  const pendingNav = useRef(null);

  useEffect(() => {
    Promise.all([
      fetch('/api/tags', { credentials: 'include' }).then(r => r.json()).catch(() => []),
      fetch('/api/inspire/status', { credentials: 'include' }).then(r => r.json()).catch(() => ({}))
    ]).then(([tagData, status]) => {
      setTags(Array.isArray(tagData) ? tagData : []);
      setUnfinishedCount(status.unflagged || 0);
      setYesCount(status.yes || 0);
      setServerHasUnfinished(!!(status.yes > 0 || status.no > 0));
      setLoading(false);
    });
  }, []);

  // Block browser back button and tab close during active session
  useEffect(() => {
    if (!started || done) return;

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };

    const handlePopState = (e) => {
      // Push state back to prevent navigation
      window.history.pushState(null, '', window.location.pathname);
      setNavGuard({ destination: null });
    };

    window.history.pushState(null, '', window.location.pathname);
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [started, done]);

  // Intercept tab bar / in-app navigation
  const handleNavAttempt = useCallback((destination) => {
    if (started && !done) {
      pendingNav.current = destination;
      setNavGuard({ destination });
    } else {
      navigate(destination);
    }
  }, [started, done, navigate]);

  // Expose handleNavAttempt so the tab bar can call it
  useEffect(() => {
    window.__gleanNavGuard = handleNavAttempt;
    return () => { delete window.__gleanNavGuard; };
  }, [handleNavAttempt]);

  const startSession = async (resume = false) => {
    setLoading(true);
    if (!resume) {
      await fetch('/api/inspire/reset', { method: 'POST', credentials: 'include' });
    }
    const params = filterTag ? `?tag=${encodeURIComponent(filterTag)}` : '';
    const resumeParam = resume ? (filterTag ? '&' : '?') + 'unflagged=1' : '';
    const res = await fetch(`/api/inspire${params}${resumeParam}`, { credentials: 'include' });
    const data = await res.json();
    const cardList = Array.isArray(data) ? data : [];
    setCards(cardList);
    const existingFlags = {};
    cardList.forEach(c => { if (c.inspire_flag !== 'neutral') existingFlags[c.id] = c.inspire_flag; });
    setFlags(existingFlags);
    setIndex(0);
    setDone(cardList.length === 0);
    setStarted(true);
    setLoading(false);
  };

  const flag = async (cardId, val) => {
    setFlags(prev => ({ ...prev, [cardId]: val }));
    await fetch(`/api/cards/${cardId}`, {
      method: 'PATCH', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ inspire_flag: val })
    });
  };

  const handleYes = async () => {
    setSwipeClass('swiping-yes');
    await flag(cards[index].id, 'yes');
    setTimeout(() => { setSwipeClass(''); advance(); }, 200);
  };

  const handleNo = async () => {
    setSwipeClass('swiping-no');
    await flag(cards[index].id, 'no');
    setTimeout(() => { setSwipeClass(''); advance(); }, 200);
  };

  const handleSkip = () => {
    const newCards = [...cards];
    const [skipped] = newCards.splice(index, 1);
    newCards.push(skipped);
    setCards(newCards);
    if (index >= newCards.length) setDone(true);
  };

  const advance = () => {
    if (index + 1 >= cards.length) setDone(true);
    else setIndex(i => i + 1);
  };

  const handleBack = () => {
    if (index > 0) setIndex(i => i - 1);
  };

  const doExport = async () => {
    setExporting(true);
    const res = await fetch('/api/inspire/export', { method: 'POST', credentials: 'include' });
    const data = await res.json();
    const blob = new Blob([data.text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gleaner-export-${new Date().toISOString().slice(0, 10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    setExporting(false);
    return data.count;
  };

  const handleExport = async () => {
    await doExport();
    setExportDone(true);
  };

  const handleTagYes = async () => {
    if (!tagInput.trim()) return;
    setTagging(true);
    await fetch('/api/inspire/tag-yes', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tagName: tagInput.trim() })
    });
    setTagging(false);
    setTagSuccess(true);
    setTagInput('');
  };

  const handleReset = async () => {
    await fetch('/api/inspire/reset', { method: 'POST', credentials: 'include' });
    setShowConfirmReset(false);
    setStarted(false);
    setDone(false);
    setUnfinishedCount(0);
    setYesCount(0);
    setServerHasUnfinished(false);
  };

  // Navigation guard actions
  const guardKeepSorting = () => {
    setNavGuard(null);
    pendingNav.current = null;
  };

  const guardSaveAndLeave = () => {
    // Progress is already saved to DB — just navigate away
    setStarted(false);
    setNavGuard(null);
    const dest = pendingNav.current;
    pendingNav.current = null;
    if (dest) navigate(dest);
  };

  const guardExportAndLeave = async () => {
    await doExport();
    setStarted(false);
    setDone(false);
    setServerHasUnfinished(false);
    setNavGuard(null);
    const dest = pendingNav.current;
    pendingNav.current = null;
    if (dest) navigate(dest);
  };

  const guardExitAndLose = async () => {
    await fetch('/api/inspire/reset', { method: 'POST', credentials: 'include' });
    setStarted(false);
    setDone(false);
    setServerHasUnfinished(false);
    setNavGuard(null);
    const dest = pendingNav.current;
    pendingNav.current = null;
    if (dest) navigate(dest);
  };

  const currentYesCount = Object.values(flags).filter(v => v === 'yes').length;
  const hasUnfinished = serverHasUnfinished;

  // NAV GUARD MODAL
  const NavGuardModal = () => (
    <div className="modal-overlay">
      <div className="modal">
        <h3>Leave this session?</h3>
        <p>You have {currentYesCount} yes card{currentYesCount !== 1 ? 's' : ''} and are {index + 1} of {cards.length} through your deck.</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginTop: '0.5rem' }}>
          <button className="btn btn-primary" onClick={guardKeepSorting}>Keep gleaning</button>
          <button className="btn btn-secondary" onClick={guardSaveAndLeave}>Save progress and leave</button>
          {currentYesCount > 0 && (
            <button className="btn btn-success" onClick={guardExportAndLeave} disabled={exporting}>
              {exporting ? 'Exporting...' : `Export ${currentYesCount} yes card${currentYesCount !== 1 ? 's' : ''} and leave`}
            </button>
          )}
          <button className="btn btn-danger" onClick={guardExitAndLose}>Exit and lose progress</button>
        </div>
      </div>
    </div>
  );

  // START SCREEN
  if (!started) {
    if (loading) return <div className="inspire-container"><div className="spinner" /></div>;

    return (
      <div className="inspire-container">
        <div style={{ textAlign: 'center', maxWidth: 440, padding: '0 1rem' }}>
          <div className="page-header">
            <h1>Glean</h1>
            <div className="subtitle">Flip through your deck. Yes, No, or Skip.</div>
          </div>

          {hasUnfinished && (
            <div style={{
              background: '#fff8f5', border: '0.5px solid var(--accent-light)',
              borderLeft: '3px solid var(--accent)', borderRadius: 'var(--radius)',
              padding: '1rem 1.25rem', marginBottom: '1.5rem', textAlign: 'left'
            }}>
              <div style={{ fontWeight: 500, fontSize: '0.95rem', marginBottom: '0.25rem' }}>Unfinished session</div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: '1rem', fontFamily: 'sans-serif' }}>
                {unfinishedCount} card{unfinishedCount !== 1 ? 's' : ''} not yet reviewed{yesCount > 0 ? `, ${yesCount} marked yes` : ''}.
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <button className="btn btn-primary" onClick={() => startSession(true)} disabled={loading}>Resume session</button>
                <button className="btn btn-secondary" onClick={() => setShowConfirmReset(true)} disabled={loading}>Start fresh</button>
              </div>
            </div>
          )}

          {!hasUnfinished && (
            <>
              {tags.length > 0 && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <label className="form-label">Filter by tag (optional)</label>
                  <div className="tag-filters" style={{ justifyContent: 'center', marginBottom: 0 }}>
                    <span className={`tag ${!filterTag ? 'active' : ''}`} onClick={() => setFilterTag('')}>All cards</span>
                    {tags.map(t => (
                      <span key={t.id} className={`tag ${filterTag === t.name ? 'active' : ''}`} onClick={() => setFilterTag(t.name)}>{t.name}</span>
                    ))}
                  </div>
                </div>
              )}
              <button className="btn btn-primary" onClick={() => startSession(false)} disabled={loading} style={{ fontSize: '1.05rem', padding: '0.75rem 2rem' }}>
                Start Session
              </button>
            </>
          )}
        </div>

        {showConfirmReset && (
          <div className="modal-overlay">
            <div className="modal">
              <h3>Start fresh?</h3>
              <p>This will clear your {yesCount} yes card{yesCount !== 1 ? 's' : ''} and all other flags.</p>
              <div className="modal-actions">
                <button className="btn btn-secondary" onClick={() => setShowConfirmReset(false)}>Cancel</button>
                <button className="btn btn-danger" onClick={handleReset}>Start fresh</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // DONE SCREEN
  if (done) {
    return (
      <div className="inspire-container">
        <div className="inspire-complete" style={{ maxWidth: 440, width: '100%' }}>
          <h2>Session complete.</h2>
          <p>
            {cards.length} card{cards.length !== 1 ? 's' : ''} reviewed
            {currentYesCount > 0 ? ` — ${currentYesCount} marked yes` : ', none marked yes'}.
          </p>

          {/* Export */}
          <div className="post-glean-section">
            <button
              className="btn btn-primary"
              onClick={handleExport}
              disabled={exporting || currentYesCount === 0}
              style={{ width: '100%' }}
            >
              {exporting ? 'Exporting…' : `Export ${currentYesCount} yes card${currentYesCount !== 1 ? 's' : ''}`}
            </button>
            {exportDone && <div className="post-glean-success">Downloaded.</div>}
          </div>

          {/* Tag all yes cards */}
          <div className="post-glean-section">
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                className="search-input"
                type="text"
                placeholder="Tag name"
                value={tagInput}
                onChange={e => { setTagInput(e.target.value); setTagSuccess(false); }}
                disabled={currentYesCount === 0}
                style={{ flex: 1 }}
              />
              <button
                className="btn btn-secondary"
                onClick={handleTagYes}
                disabled={tagging || !tagInput.trim() || currentYesCount === 0}
              >
                {tagging ? 'Applying…' : 'Apply tag'}
              </button>
            </div>
            {tagSuccess && (
              <div className="post-glean-success">
                Tag applied to {currentYesCount} card{currentYesCount !== 1 ? 's' : ''}.
              </div>
            )}
          </div>

          {/* Reset */}
          <div className="post-glean-divider" />
          <div className="post-glean-section">
            <button
              className="btn btn-danger"
              onClick={() => setShowConfirmReset(true)}
              style={{ width: '100%' }}
            >
              Reset all flags
            </button>
          </div>
        </div>

        {showConfirmReset && (
          <div className="modal-overlay">
            <div className="modal">
              <h3>Reset all flags?</h3>
              <p>This will clear all Yes/No flags. You won't be able to export or tag these cards afterward.</p>
              <div className="modal-actions">
                <button className="btn btn-secondary" onClick={() => setShowConfirmReset(false)}>Cancel</button>
                <button className="btn btn-danger" onClick={handleReset}>Reset</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // SESSION SCREEN
  const card = cards[index];

  return (
    <div className="inspire-container">
      {navGuard && <NavGuardModal />}

      <div className="inspire-progress">
        {index + 1} of {cards.length} — {currentYesCount} yes
      </div>

      <div className={`inspire-card ${swipeClass}`}>
        <div className="inspire-card-text">{card.body}</div>
      </div>

      <div className="inspire-primary-buttons">
        <button className="btn-inspire-no" onClick={handleNo}>No</button>
        <button className="btn-inspire-yes" onClick={handleYes}>Yes</button>
      </div>

      <div className="inspire-secondary-buttons">
        <button className="btn-inspire-text" onClick={handleBack} disabled={index === 0}>Back</button>
        <button className="btn-inspire-text" onClick={handleSkip}>Skip</button>
      </div>
    </div>
  );
}
